import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import { Heading, Text } from 'tio-ui/components/typography';
import Button from 'tio-reimagined/components/button';
import { or } from 'tio-ui/utilities';
import {
  PhTrophy,
  PhStar,
  PhRocket,
  PhTarget,
  PhCrown,
  PhMedal,
  PhCheckCircle,
  PhShare,
  PhDownload,
  PhFlag,
} from 'ember-phosphor-icons';
import type { PslfAchievement } from 'tio-employee/services/career-pathways-api';

interface MilestoneBadgeSignature {
  Args: {
    achievement: PslfAchievement;
    onShare?: (achievement: PslfAchievement) => void;
    onDownload?: (achievement: PslfAchievement) => void;
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

// Map milestone types to icons and colors
function getMilestoneStyle(type: PslfAchievement['milestoneType']) {
  const styles: Record<string, { icon: typeof PhTrophy; gradient: string }> = {
    started: { icon: PhFlag, gradient: 'from-blue-500 to-blue-700' },
    first_year: { icon: PhStar, gradient: 'from-amber-500 to-amber-700' },
    two_years: { icon: PhTarget, gradient: 'from-emerald-500 to-emerald-700' },
    four_years: { icon: PhMedal, gradient: 'from-purple-500 to-purple-700' },
    halfway: { icon: PhRocket, gradient: 'from-cyan-500 to-blue-600' },
    six_years: { icon: PhTrophy, gradient: 'from-indigo-500 to-indigo-700' },
    eight_years: { icon: PhTrophy, gradient: 'from-pink-500 to-pink-700' },
    home_stretch: { icon: PhRocket, gradient: 'from-orange-500 to-red-600' },
    final_countdown: { icon: PhTarget, gradient: 'from-yellow-400 to-amber-600' },
    forgiveness: { icon: PhCrown, gradient: 'from-yellow-300 via-amber-400 to-yellow-500' },
  };
  return styles[type] || styles.started;
}

export default class PslfMilestoneBadge extends Component<MilestoneBadgeSignature> {
  @tracked showConfetti = false;

  get style() {
    return getMilestoneStyle(this.args.achievement.milestoneType);
  }

  get isUnlocked() {
    return this.args.achievement.achievedAt !== null;
  }

  get formattedDate() {
    if (!this.args.achievement.achievedAt) return null;
    return new Date(this.args.achievement.achievedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get isForgiveness() {
    return this.args.achievement.milestoneType === 'forgiveness';
  }

  handleShare = () => {
    this.args.onShare?.(this.args.achievement);
  };

  handleDownload = () => {
    this.args.onDownload?.(this.args.achievement);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if @compact}}
      {{! Compact badge view for grids }}
      <div class="relative group cursor-pointer" title="{{@achievement.badgeName}}" ...attributes>
        <div
          class="w-16 h-16 rounded-full flex items-center justify-center
            {{if
              this.isUnlocked
              (concat 'bg-gradient-to-br ' this.style.gradient)
              'bg-gray-700/50'
            }}
            {{unless this.isUnlocked 'opacity-40'}}
            shadow-lg transform transition-transform group-hover:scale-110"
        >
          {{#if this.isUnlocked}}
            <span class="text-2xl">{{@achievement.badgeEmoji}}</span>
          {{else}}
            <this.style.icon @size={{28}} class="text-gray-500" />
          {{/if}}
        </div>
        {{#unless this.isUnlocked}}
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="w-16 h-16 rounded-full border-2 border-dashed border-gray-600"></div>
          </div>
        {{/unless}}
        {{#if this.isForgiveness}}
          <div class="absolute -top-1 -right-1">
            <span class="text-lg">👑</span>
          </div>
        {{/if}}
      </div>
    {{else}}
      {{! Full achievement card }}
      <div
        class="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 rounded-2xl overflow-hidden shadow-xl"
        ...attributes
      >
        {{! Header with badge }}
        <div
          class="relative h-32 flex items-center justify-center
            {{if this.isUnlocked (concat 'bg-gradient-to-br ' this.style.gradient) 'bg-gray-800'}}"
        >
          {{! Decorative elements }}
          {{#if this.isUnlocked}}
            <div class="absolute inset-0 overflow-hidden">
              <div class="absolute top-4 left-4 w-12 h-12 bg-white/10 rounded-full blur-xl"></div>
              <div
                class="absolute bottom-4 right-8 w-16 h-16 bg-white/10 rounded-full blur-xl"
              ></div>
            </div>
          {{/if}}

          {{! Badge icon }}
          <div
            class="relative z-10 w-20 h-20 rounded-full flex items-center justify-center
              {{if this.isUnlocked 'bg-white/20 backdrop-blur-sm' 'bg-gray-700/50'}}
              border-4
              {{if this.isUnlocked 'border-white/30' 'border-gray-600'}}"
          >
            {{#if this.isUnlocked}}
              <span class="text-4xl">{{@achievement.badgeEmoji}}</span>
            {{else}}
              <this.style.icon @size={{40}} class="text-gray-500" />
            {{/if}}
          </div>

          {{! Payment count badge }}
          {{#if this.isUnlocked}}
            <div
              class="absolute top-4 right-4 px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide text-white bg-white/20"
            >
              {{@achievement.payments}}
              payments
            </div>
          {{/if}}

          {{! Locked overlay }}
          {{#unless this.isUnlocked}}
            <div class="absolute inset-0 flex items-center justify-center bg-black/40">
              <span class="text-4xl opacity-60">🔒</span>
            </div>
          {{/unless}}
        </div>

        {{! Content }}
        <div class="p-5">
          <div class="text-center mb-4">
            <Heading
              @level={{3}}
              class="text-white font-bold text-lg {{unless this.isUnlocked 'opacity-60'}}"
            >
              {{@achievement.badgeName}}
            </Heading>
            <Text class="text-gray-400 text-sm mt-1 {{unless this.isUnlocked 'opacity-60'}}">
              {{@achievement.message}}
            </Text>
          </div>

          {{#if this.isUnlocked}}
            {{! Unlocked state }}
            <div class="flex items-center justify-center gap-2 text-emerald-400 text-sm mb-4">
              <PhCheckCircle @size={{16}} class="text-emerald-400" />
              <span>Unlocked {{this.formattedDate}}</span>
            </div>

            {{! Stats if available }}
            {{#if @achievement.statsAtMilestone}}
              <div class="bg-black/20 rounded-lg p-3 mb-4">
                <div class="grid grid-cols-2 gap-3 text-center">
                  <div>
                    <Text class="text-white font-bold text-lg">
                      {{@achievement.statsAtMilestone.percentComplete}}%
                    </Text>
                    <Text class="text-gray-500 text-xs">Complete</Text>
                  </div>
                  <div>
                    <Text class="text-white font-bold text-lg">
                      ${{@achievement.statsAtMilestone.forgivenessEarned}}
                    </Text>
                    <Text class="text-gray-500 text-xs">Earned</Text>
                  </div>
                </div>
              </div>
            {{/if}}

            {{! Share/Download actions }}
            {{#if (or @onShare @onDownload)}}
              <div class="flex gap-2">
                {{#if @onShare}}
                  <Button
                    @intent="primary"
                    @appearance="minimal"
                    class="flex-1 justify-center"
                    {{on "click" this.handleShare}}
                  >
                    <PhShare @size={{16}} />
                    <span>Share</span>
                  </Button>
                {{/if}}
                {{#if @onDownload}}
                  <Button
                    @intent="primary"
                    @appearance="minimal"
                    class="flex-1 justify-center"
                    {{on "click" this.handleDownload}}
                  >
                    <PhDownload @size={{16}} />
                    <span>Certificate</span>
                  </Button>
                {{/if}}
              </div>
            {{/if}}
          {{else}}
            {{! Locked state - show target payments }}
            <div class="text-center">
              <Text class="text-gray-500 text-sm">
                Reach
                {{@achievement.payments}}
                payments to unlock
              </Text>
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
